import { ContactInfo } from './contactInfo';
import { User } from './user';
import { Proposal } from './proposal';
import { Project } from './project';
import { WorkOrder } from './workOrder';
import { Item } from './item';
import { Address } from './address';

// EXPLICIT values that MATCH the backend (WeakEntities.cs InvoiceStatus). These were
// positional before and drifted for every value > 1 (Pending/Paid/PaidEntered/DoNotEnter
// were mislabeled over the wire — a backend Paid=2 read as frontend Pending, etc.). Aligning
// them corrects every read AND write across AR/KPIs/deposits/status menus. Keep in lockstep
// with the backend enum.
export enum InvoiceStatus {
    New = 0,
    NewEntered = 1,
    Paid = 2,
    PaidEntered = 3,
    DoNotEnter = 4,
    Pending = 5,
    WrittenOff = 6, // item 485 — leaves AR, stays in ledger + Invoices behind a filter
}

// item 626 — THE single human label for an invoice status. Hoisted here (was a private
// copy in InvoiceGrid) so the grid, project view, AR/Pipeline reports, and the accountant
// export all read one word for one status — no more "Entered" vs "New Entered" vs
// "NewEntered" for InvoiceStatus.NewEntered. Never humanize the raw enum name for invoices;
// call this instead.
export const invoiceStatusLabel = (value: InvoiceStatus): string => {
  if (value === InvoiceStatus.NewEntered) return 'Entered';
  // item 553 — PaidEntered = the accountant's "Deposited" state (New → Entered → Deposited)
  if (value === InvoiceStatus.PaidEntered) return 'Deposited';
  if (value === InvoiceStatus.WrittenOff) return 'Written off';
  if (value === InvoiceStatus.DoNotEnter) return 'Do not enter';
  return InvoiceStatus[value] ?? String(value);
};

// item 579 — automatic Stripe payment flag (backend WeakEntities.cs StripePaymentStatus —
// keep in lockstep). Set when a Stripe payment completes (item-32 sandbox stub until
// go-live). Independent of InvoiceStatus: a Stripe-paid invoice leaves open AR (the money
// is in) but stays "Pending accountant" in the Deposits ledger until confirmed.
export enum StripePaymentStatus {
    None = 0,
    AchPaid = 1,
    CardPaid = 2,
}

export interface Invoice {
    invoiceNumber: string;
    // item 465 — Service Address (end-user); serviceName is the required name, rest optional
    serviceName?: string;
    serviceAddress?: Address;
    servicePhone?: string;
    serviceEmail?: string;
    date: string;
    customerId: string;
    projectId: string;
    proposalId: string;
    workOrderId: string;
    // item 609 — the source work order's number = the "job code" (read-only, pass-through)
    workOrderNumber?: string;
    pointOfContact: string;
    categoryNames: string;
    salesRepId: string;
    discount: number;
    discountReason: string;
    internalNotes: string;
    // item 543 — client-facing narrative on the invoice PDF (Internal Notes never prints)
    reasonForWork?: string;
    workPerformed?: string;
    // item 733 (Chad) — the CLIENT PO number (required at finalize, printed on the invoice),
    // exclude-scope output toggle, SENT tracking, + approver attribution.
    customerPoNumber?: string;
    trackingUrl?: string | null; // item 1310 — shipment tracking link (from the SO); PDF + email hyperlink
    showPerformedOnPdf?: boolean; // item 1336 — T&M output option
    // item 1488 — opt-in output toggles (replace excludeScopeFromPdf); editable after finalize
    includeScopeOnPdf?: boolean;
    includeWorkPerformedOnPdf?: boolean;
    sentDate?: string | null;
    sentById?: string | null;
    clientApprovedByName?: string | null;
    approvedByName?: string | null;
    finalizedByName?: string | null; // item 817 — WHO finalized
    itemize: boolean;
    // item 597 — platform PASS-THROUGH (no invoice column): the GET payload computes it from
    // the source WO/proposal (effectivePlatform); index rows carry `platform`. Display-only.
    effectivePlatform?: string | null;
    platform?: string | null;
    invoiceItems: InvoiceItem[];
    // no
    id: string;
    salesRepName: string;
    createdById: string;
    createdDate: string;
    lastModifiedById: string;
    lastModifiedDate: string;
    status: InvoiceStatus;
    isArchive: boolean;
    isVoid: boolean;
    isFinalized: boolean; // item 38: Draft vs Finalized (locked, void-only)
    isLocked: boolean;
    quickBooksReference?: string; // item 28/29: set when the accountant enters it
    // item 574 — deposit capture (item 480), now projected on the index rows so the
    // Deposits ledger's METHOD column actually lights up
    depositMethod?: string | null;
    depositReference?: string | null;
    depositedDate?: string | null;
    depositCheckFileId?: string | null; // item 574 — stored image/PDF of the physical check
    // item 586 — the accountant who VERIFIED the deposit (Mark deposited), from the dedicated
    // DepositedById column (LastModifiedById gets overwritten later). Shown on deposited rows.
    depositedByName?: string | null;
    // item 579 — automatic Stripe payment flag + the Stripe payment reference (index rows)
    stripePaymentStatus?: StripePaymentStatus;
    stripePaymentReference?: string | null;
    // item 1038 — manual payment ARRIVAL (check/wire in hand), distinct from the deposit
    // confirm: the invoice reads PAID + leaves open AR the moment the money exists
    paidDate?: string | null;
    // item 1563 — written-off part of the balance (retailTotal is already net of it)
    writeOffAmount?: number;
    creditAppliedAmount?: number; // item 1575 — credit memo applied after finalize; retailTotal is already net of it
    // item 581 — the payment-failed hint (a Stripe payment attempt failed; cleared by a
    // later success) + the refunded-after-deposit needs-review flag for the accountant
    stripePaymentFailedDate?: string | null;
    stripeRefundNeedsReview?: boolean;
    // item 1593 — a Stripe bank debit is clearing (started when); Mark paid/deposited + pay links refuse
    stripeProcessingDate?: string | null;
    customerName?: string; // list projection carries it
    projectName?: string; // item 627 — list projection carries it (SelectInvoiceIndexItemDto)
    enteredDate?: string | null; // item 340 — permanent entry stamp
    enteredByName?: string | null; // item 340
    // item 74: the customer's current sales attribution (item 72), for attribution reports
    outsideSalesRepName?: string;
    insideSalesRepName?: string;
    referralName?: string;
    customer: ContactInfo;
    project: Project;
    proposal: Proposal;
    workOrder: WorkOrder;
    salesRep: User;
    createdBy: User;
    lastModifiedBy: User;
    retailSubtotal: number;
    taxTotal: number;
    costTotal: number;
    retailTotal: number;
    taxState?: string; // item 179
}

export interface InvoiceItem {
    description: string;
    costEach: number;
    retailEach: number;
    quantity: number;
    taxable: boolean;
    employeeId: string;
    lineDate?: string | null; // item 1336 — per-line T&M date
    // item 1452 — pull this line from on-hand at finalize (null = undecided/legacy, false = pass-through)
    pullFromStock?: boolean | null;
    stockPulledDate?: string | null; // item 1452 — set once finalize actually decremented on-hand
    // item 1484b — BACKORDERED: units to follow (null = not backordered; the line's quantity is 0 while set)
    backorderedQty?: number | null;
    // no
    id: string;
    vendorId?: string;
    brand: string;
    sku: string;
    createdById: string;
    createdDate: string;
    lastModifiedById: string;
    lastModifiedDate: string;
    vendor?: ContactInfo;
    createdBy?: User;
    lastModifiedBy?: User;
    itemId: string;
    category: string;
    costSubtotal: number;
    retailSubtotal: number;
    taxTotal: number;
    item?: Item;
    invoiceId: string;
    costTotal: number;
    retailTotal: number;
    invoice?: Invoice;
    employee?: User;
    isLocked: boolean;

    // might delete
    tax: number;
    // item 436 — MSRP/SRP suggestion (editor pair; never the billed figure)
    listPrice?: number | null;
    isHardware?: boolean;
    // item 1290 (Chad: "make the discount feature on invoices match the sales orders") — the
    // standard group price before a 1234/1239 discount, carried over from the source WO/SO line.
    // The shared items form reads it and shows "std $X / −5% = $Y" in the price cell, and the
    // invoice PDF prints the same per-line math + pricing ledger the sales order does.
    preDiscountRetailEach?: number | null;
}

export interface InvoicesDto {
    index: Invoice[];
    length: number;
}
